import { useState, useEffect } from 'react';
import SocialProof from "@/components/common/SocialProof";
import { Button } from "@/components/ui/button";
import { useTranslation, Trans } from "react-i18next";
import { IconCopy, IconCheck, IconX } from "@tabler/icons-react";
import { usePromoCode, getPromoWithNearestExpiry } from "@/hooks/use-promo-code";
import Countdown from "@/components/common/Countdown";
import { shouldShowCountdown } from "@/lib/countdown";

interface PromoOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  onRevealCode: (email: string) => void;
  onComplete: () => void;
}

// TODO: should be moved to modals?
export const PromoOverlay = ({ isOpen, onClose, onRevealCode, onComplete }: PromoOverlayProps) => {
  const initialStep = 1;
  const { t } = useTranslation();
  const [step, setStep] = useState(initialStep);
  const [email, setEmail] = useState('');
  const [copied, setCopied] = useState(false);

  const { eventCode, eventData, referralCode, referralData } = usePromoCode();
  const countdownData = getPromoWithNearestExpiry(eventData, referralData);
  const countdownTargetDate = countdownData?.expires_at
    ? new Date(countdownData.expires_at * 1000)
    : null;

  // useEffect(() => {
  //   if (isOpen) {
  //     setStep(initialStep);
  //     setEmail('');
  //   }
  // }, [isOpen, initialStep]);

  const handleCopy = () => {
    navigator.clipboard.writeText('WELCOME10');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleClose = () => {
    setStep(initialStep);
    setEmail('');
    onClose();
  };

  const handleComplete = () => {
    setStep(initialStep);
    onComplete();
  }

  // const heroImage = t('assets.heroImage') || "https://cdn-site.clarymind.com/product-page-app/video-cover/cover-zh_TW-3.jpg";
  // const heroImage = "https://cdn-site.clarymind.com/product-page-app/benefits/benefit-01.jpg";
  const heroImage = "https://cdn-site.clarymind.com/product-page-app/video-cover/cover-zh_TW-3.jpg";

  return (
    <div
      className={`fixed inset-0 z-[9999] flex items-center justify-center w-full h-full transition-opacity duration-300 ${isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
      aria-hidden={!isOpen}
    >

      <div className="absolute bg-black/80 w-full h-full" onClick={handleClose} />

      <div className={`bg-white/95 backdrop-blur-sm w-[90vw] h-[82vh] max-w-4xl max-h-[700px] rounded-3xl overflow-hidden relative flex flex-col md:flex-row transition-all duration-300 transform ${isOpen ? 'scale-100 opacity-100' : 'scale-95 opacity-0'}`}>
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 z-10 w-8 h-8 flex items-center justify-center bg-black/20 text-white rounded-full"
        >
          <IconX size={18} />
        </button>

        <div className="w-full h-1/4 md:h-full md:w-1/2 lg:w-1/2 relative md:order-last shrink [@media(max-height:680px)]:hidden">
          <img src={heroImage} alt="Clarymind" className="w-full h-full object-cover md:object-center" />
        </div>

        <div className="flex-1 px-6 pb-8 pt-4 sm:pt-6 md:p-12 flex flex-col items-center justify-center shrink-0">
          <div className="w-full max-w-md">
            {step === 1 && (
              <div className="flex flex-col items-center justify-center space-y-2 pb-4">
                <img
                  src="/brands/clarymind-logo_logotype-app-transparent-light-outline.svg"
                  alt="Clarymind"
                  className="h-10 w-auto"
                />
                <div className="flex flex-col gap-3 sm:gap-4 pt-1 sm:pt-2">
                  <h3 className="text-2xl py-1 px-4 font-bold text-center text-slate-900 leading-snug whitespace-pre-line">
                    <Trans i18nKey='promoOverlay.title' />
                    {/* {t('promoOverlay.title')} */}
                  </h3>
                  <h2 className="text-lg font-semibold text-center text-slate-700">
                    {t('promoOverlay.question')}
                  </h2>
                </div>
                <div className="flex flex-col gap-1.5 sm:gap-2 px-6 relative w-full items-center">
                  {['allTheTime', 'frequently', 'sometimes', 'rarely'].map((key) => (
                    <Button
                      key={key}
                      variant="default"
                      className="w-full justify-center text-base text-center h-auto py-2.5 sm:py-3 px-4 text-slate-900 rounded-xl bg-white border-slate-900 border border-b-4"
                      onClick={() => setStep(2)}
                    >
                      {t(`promoOverlay.options.${key}`)}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="flex flex-col items-center justify-center space-y-4 animate-in fade-in slide-in-from-bottom-4 pb-4">
                <img
                  src="/brands/clarymind-logo_logotype-app-transparent-light-outline.svg"
                  alt="Clarymind"
                  className="h-10 w-auto"
                />
                <div className="flex flex-col gap-0 pb-2 px-8">
                  {/* <h3 className="text-lg py-1 px-4 font-bold text-center text-sky-500">
                    {t('promoOverlay.title')}
                  </h3> */}
                  <h2 className="text-2xl font-bold text-center text-slate-900">
                    {/* {t('promoOverlay.emailHint')} */}
                    <Trans i18nKey='promoOverlay.emailHint' />
                  </h2>
                </div>
                <div className="w-full px-6 space-y-3 mt-6">
                  <input
                    type="email"
                    placeholder="Email"
                    className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-600 focus:border-transparent transition-all"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                  <Button
                    className="w-full h-12 text-bold rounded-lg"
                    disabled={!email || !email.includes('@')}
                    onClick={() => {
                      onRevealCode(email);
                      setStep(3);
                    }}
                  >
                    {t('promoOverlay.continue')}
                  </Button>
                </div>
                <div className="w-full h-0.5 px-6 pt-3 pb-4">
                  <div className="w-full h-0.5 border-b border-slate-300" />
                </div>
                <SocialProof darkBackground={false} forceBreak={true} />
              </div>
            )}

            {step === 3 && (
              <div className="space-y-2 text-center animate-in zoom-in-95 fade-in duration-300 pt-2 pb-4">
                <div className="flex flex-col gap-2 pb-4">
                  <h2 className="text-2xl font-bold text-slate-900">{t('promoOverlay.successTitle')}</h2>
                  <p className="text-slate-600">{t('promoOverlay.successMessage')}</p>
                </div>

                <div className="bg-slate-100 border-2 border-dashed border-slate-200 rounded-2xl pl-4 py-2.5 flex items-center group justify-center sm:flex-row gap-3">
                  <span className="text-3xl font-mono font-bold text-slate-800 tracking-wider">WELCOME10</span>
                  <Button
                    variant="default"
                    size="icon"
                    className="w-12 h-12 shrink-0 bg-transparent hover:bg-slate-200/50 rounded-xl"
                    onClick={handleCopy}
                  >
                    {copied ? (
                      <IconCheck size={36} className="text-teal-500" />
                    ) : (
                      <IconCopy size={36} className="text-slate-500" />
                    )}
                  </Button>
                </div>

                {countdownTargetDate && countdownData?.valid && shouldShowCountdown(countdownTargetDate) && (
                  <div className="pb-6 w-full flex flex-col items-center justify-center">
                    <Countdown
                      targetDate={countdownTargetDate}
                      textColor="text-slate-700"
                      numberColor="text-red-600"
                      promoCode={eventCode || referralCode} />
                  </div>
                )}

                <Button
                  className="w-full h-12 text-bold rounded-lg"
                  onClick={handleComplete}
                >
                  {t('promoOverlay.shopNow')}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
